/**
 * AppHostUtil
 *
 * Recognises the regional app.justwalkout.com hosts, `{awsRegion}.{stage}.app.justwalkout.com`
 * (e.g. eu-west-1.prod.app.justwalkout.com), and lets the landing URL carry the `domain` and
 * `region` query parameters the rest of the app reads through EnvironmentDetail.
 *
 * A visitor who types or bookmarks a regional host without those parameters would otherwise start
 * with no environment. Deriving them from the hostname keeps every existing reader unchanged.
 *
 * This module must stay import-free (type-only imports are fine). The bootstrap module
 * (src/bootstrap/normalizeLandingUrl.ts) imports it ahead of everything else and calls
 * normalizeLandingUrl at import time, so anything this module imports is evaluated before the URL
 * is normalized. Anything that transitively imports @amzn/ihm-associate-tools-js-lib -- including
 * @utils/RegionRealms -- is unsafe: that library builds its service clients at load time from
 * location.search, so importing it here would fix them to a URL with no domain/region. That is
 * why the airport/realm table below is a local copy rather than RegionRealms' AIRPORT_CODES/toRealm;
 * test/utils/AppHostUtil.test.ts keeps the two in step.
 */

const APP_HOST_SUFFIX = '.app.justwalkout.com'

/**
 * Airport code -> AWS region for every cell the platform is deployed to. Must equal
 * RegionRealms.AIRPORT_CODES mapped through RegionRealms.toRealm (asserted in the tests); a local
 * copy only because importing RegionRealms would load @amzn/ihm-associate-tools-js-lib (see above).
 */
export const AIRPORT_TO_REALM: Readonly<Record<string, string>> = {
    iad: 'us-east-1',
    pdx: 'us-west-2',
    dub: 'eu-west-1',
    sin: 'ap-southeast-1',
}

/** True when `code` is one of AIRPORT_TO_REALM's own keys (not an inherited Object property). */
const isAirportCode = (code: string): boolean => Object.prototype.hasOwnProperty.call(AIRPORT_TO_REALM, code)

const APP_STAGES: readonly string[] = ['alpha', 'test', 'gamma', 'prod']

export interface IAppHostInfo {
    /** Deployment stage, used as the `domain` query parameter (e.g. `prod`). */
    readonly stage: string
    /** AWS region of the host (e.g. `eu-west-1`). */
    readonly realm: string
    /** Airport code of the host, used as the `region` query parameter (e.g. `dub`). */
    readonly airport: string
}

/**
 * Parses a regional app host. Returns null for any other host, including the bare
 * app.justwalkout.com, the legacy storemanagement.jihm.amazon.dev hosts, and unknown regions or stages.
 *
 * The realm slot must equal AIRPORT_TO_REALM[airport] for some airport code, so only a real realm
 * is accepted there: iad.prod.app.justwalkout.com is rejected even though 'iad' is an airport code.
 */
export function parseAppHost (hostname: string | null | undefined): IAppHostInfo | null {
    if (!hostname) return null
    const host = hostname.toLowerCase()
    if (!host.endsWith(APP_HOST_SUFFIX)) return null

    const labels = host.slice(0, -APP_HOST_SUFFIX.length).split('.')
    if (labels.length !== 2) return null

    const [realm, stage] = labels
    if (!APP_STAGES.includes(stage)) return null

    const airport = Object.keys(AIRPORT_TO_REALM).find((code) => AIRPORT_TO_REALM[code] === realm)
    if (!airport) return null

    return { stage, realm, airport }
}

/** Stages whose sign-in page offers a region switch: the stages with a bare domain. */
export type SignInStage = 'prod' | 'gamma'

/** The three choices the sign-in region switch offers, in display order. */
export type SignInWorldRegion = 'americas' | 'europe' | 'asiaPacific'

/** Airport codes the region switch can send a visitor to. */
export type SignInRegion = 'iad' | 'pdx' | 'dub' | 'sin'

export interface ISignInRegionOption {
    /** Stage of the target host, used as the `domain` query parameter. */
    readonly stage: SignInStage
    readonly worldRegion: SignInWorldRegion
    /** Airport code, used as the `region` query parameter (e.g. `dub`). */
    readonly region: SignInRegion
    /** AWS region of the target host (e.g. `eu-west-1`). */
    readonly realm: string
}

/**
 * Region-switch targets per stage. Prod: Americas is IAD (North America signs in through IAD; PDX
 * stores are reached in-app). Gamma has no IAD cell, so Americas is PDX.
 *
 * Must match APEX_REGION_TARGETS / GAMMA_APEX_REGION_TARGETS in JWOStoreManagementPlatformCDK's
 * lib/common/apexRedirectTargets.ts (prod: IAD, DUB, SIN; gamma: PDX, DUB, SIN) -- that CR builds
 * the bare-domain redirect that sends a visitor to one of these hosts in the first place.
 */
const SIGN_IN_REGION_OPTIONS: Readonly<Record<SignInStage, readonly ISignInRegionOption[]>> = {
    prod: [
        { stage: 'prod', worldRegion: 'americas', region: 'iad', realm: 'us-east-1' },
        { stage: 'prod', worldRegion: 'europe', region: 'dub', realm: 'eu-west-1' },
        { stage: 'prod', worldRegion: 'asiaPacific', region: 'sin', realm: 'ap-southeast-1' },
    ],
    gamma: [
        { stage: 'gamma', worldRegion: 'americas', region: 'pdx', realm: 'us-west-2' },
        { stage: 'gamma', worldRegion: 'europe', region: 'dub', realm: 'eu-west-1' },
        { stage: 'gamma', worldRegion: 'asiaPacific', region: 'sin', realm: 'ap-southeast-1' },
    ],
}

/**
 * The stage of a regional app host whose sign-in page offers a region switch (prod or gamma),
 * else null (alpha, test, the bare domains, legacy and unknown hosts).
 */
export function signInStage (appHost: IAppHostInfo | null): SignInStage | null {
    const stage = appHost?.stage
    return stage === 'prod' || stage === 'gamma' ? stage : null
}

/** The region-switch choices of a stage, in display order (Americas, Europe, Asia-Pacific). */
export function signInRegionOptions (stage: SignInStage): readonly ISignInRegionOption[] {
    return SIGN_IN_REGION_OPTIONS[stage]
}

/**
 * Landing URL of a region-switch choice's host, e.g.
 * `https://eu-west-1.prod.app.justwalkout.com/index.html?domain=prod&region=dub` or
 * `https://us-west-2.gamma.app.justwalkout.com/index.html?domain=gamma&region=pdx`.
 * Keeps the `locale` parameter from `currentSearch` and drops everything else, which belongs to
 * the region being left.
 */
export function buildSignInRegionUrl (option: ISignInRegionOption, currentSearch: string = ''): string {
    const params = new URLSearchParams({ domain: option.stage, region: option.region })
    const locale = new URLSearchParams(currentSearch).get('locale')
    if (locale) params.set('locale', locale)
    return `https://${option.realm}.${option.stage}${APP_HOST_SUFFIX}/index.html?${params.toString()}`
}

/**
 * On a regional app host, the page's own origin (e.g. `https://eu-west-1.prod.app.justwalkout.com`);
 * null on any other host. Tools, the portal and assets are served by the same distribution as the
 * shell, so on the new hosts they load same-origin (keeps `frame-ancestors 'self'` and the
 * `/assets` signed cookies scoped to the host the visitor is on).
 */
export function currentAppHostOrigin (loc: Pick<Location, 'hostname' | 'origin'> = window.location): string | null {
    return parseAppHost(loc.hostname) ? loc.origin : null
}

/**
 * Removes every variant of `key` from `params` regardless of case (e.g. `domain`, `DOMAIN`,
 * `Domain` are all removed by one call with `key = 'domain'`), so a canonical replacement can be
 * set without leaving a second, differently-cased copy behind.
 *
 * Needed because URLSearchParams itself is case-sensitive: `params.get('domain')` does not see
 * `?DOMAIN=gamma`, but EnvironmentDetail reads query params case-insensitively and would still
 * find and use that stray uppercase key even after this function sets a lowercase one.
 */
function deleteCaseInsensitive (params: URLSearchParams, key: string): void {
    const lower = key.toLowerCase()
    const matches: string[] = []
    params.forEach((_value, k) => {
        if (k.toLowerCase() === lower) matches.push(k)
    })
    matches.forEach((k) => params.delete(k))
}

/** Every `[key, value]` pair whose key matches `key` case-insensitively, in URL order. */
function entriesCaseInsensitive (params: URLSearchParams, key: string): Array<[string, string]> {
    const lower = key.toLowerCase()
    const found: Array<[string, string]> = []
    params.forEach((value, k) => {
        if (k.toLowerCase() === lower) found.push([k, value])
    })
    return found
}

/**
 * The supplied `region` to keep, lower-cased, or undefined to use the host's own airport. Kept
 * only when every supplied variant names the same airport code of AIRPORT_TO_REALM: an unknown
 * code (`xyz`) or conflicting duplicates (`region=dub&region=sin`) fall back to the host.
 */
function suppliedRegion (entries: Array<[string, string]>): string | undefined {
    const values = new Set(entries.map(([, value]) => value.toLowerCase()))
    if (values.size !== 1) return undefined
    const [region] = Array.from(values)
    return isAirportCode(region) ? region : undefined
}

/** True when `entries` is exactly one canonical lower-case `key=value` pair. */
function isCanonical (entries: Array<[string, string]>, key: string, value: string): boolean {
    return entries.length === 1 && entries[0][0] === key && entries[0][1] === value
}

/**
 * On a regional app host, normalises the landing URL's `domain` and `region` query parameters,
 * derived from the hostname. Existing parameters other than `domain`/`region` and the hash are
 * kept. Uses history.replaceState, so nothing reloads and no history entry is added.
 *
 * `domain` is always overwritten to the host's own stage, never merely filled in when missing. An
 * app host's stage is fixed by its CloudFront distribution, so a visitor to
 * `us-east-1.prod.app.justwalkout.com/?domain=alpha` must not be able to point a prod-origin shell
 * at the alpha gateway, Coral and Cognito endpoints -- the query parameter is not a trusted source
 * for which stage this host is. A supplied `region` is kept (lower-cased) when it is one of
 * AIRPORT_TO_REALM's airport codes: the IIB and IHM-login return URLs put the selected store's
 * region on the current host (e.g. `?region=pdx` on IAD). Unknown, conflicting or missing values
 * become the host's airport.
 *
 * The URL is only rewritten when that changes something: when `domain` and `region` are already
 * single canonical lower-case keys with the values above, wherever they sit in the query, the
 * URL is left alone and history.replaceState is not called.
 *
 * Both parameters are matched case-insensitively and every variant is deleted before the
 * canonical lower-case key is set, mirroring the CloudFront Function that redirects here
 * (buildQuery in JWOStoreManagementPlatformCDK lib/cloudfront-functions/apex-redirect.js, CR-313012159)
 * which lower-cases query keys for the same reason: EnvironmentDetail itself reads `domain`/`region`
 * case-insensitively, so a stray `?DOMAIN=gamma` left behind would be read ahead of (or instead of)
 * the canonical value this function sets.
 *
 * Must run before anything reads the URL's environment parameters.
 *
 * @returns true when the URL was changed.
 */
export function normalizeLandingUrl (loc: Location = window.location, hist: History = window.history): boolean {
    const info = parseAppHost(loc.hostname)
    if (!info) return false

    const params = new URLSearchParams(loc.search)
    const domainEntries = entriesCaseInsensitive(params, 'domain')
    const regionEntries = entriesCaseInsensitive(params, 'region')
    const region = suppliedRegion(regionEntries) ?? info.airport

    if (isCanonical(domainEntries, 'domain', info.stage) && isCanonical(regionEntries, 'region', region)) return false

    deleteCaseInsensitive(params, 'domain')
    deleteCaseInsensitive(params, 'region')
    params.set('domain', info.stage)
    params.set('region', region)

    const nextSearch = `?${params.toString()}${loc.hash}`

    try {
        hist.replaceState(hist.state, '', `${loc.pathname}${nextSearch}`)
        return true
    } catch (err) {
        // The app still loads; readers fall back to their own defaults.
        console.error('AppHostUtil: could not normalize domain/region on the landing URL', err)
        return false
    }
}
